import { morph } from '@theme/morph';

/**
 * A class to re-render sections using the Section Rendering API
 */
class SectionRenderer {
  /**
   * The cache of section HTML
   * @type {Map<string, string>}
   */
  #cache = new Map();

  /**
   * The abort controllers by section ID
   * @type {Map<string, AbortController>}
   */
  #abortControllersBySectionId = new Map();

  /**
   * The pending promises
   * @type {Map<string, Promise<string>>}
   */
  #pendingPromises = new Map();

  constructor() {
    window.addEventListener('load', this.#cachePageSections.bind(this));
  }

  /**
   * Renders a section
   * @param {string} sectionId - The section ID
   * @param {Object} [options] - The options
   * @param {boolean} [options.cache] - Whether to use the cache
   * @param {URL} [options.url] - The URL to render the section from
   * @returns {Promise<string>} The rendered section HTML
   */
  async renderSection(sectionId, options) {
    const { cache = !Shopify.designMode } = options ?? {};
    const { url } = options ?? {};
    this.#abortPendingMorph(sectionId);

    const abortController = new AbortController();
    this.#abortControllersBySectionId.set(sectionId, abortController);

    try {
      const sectionHTML = await this.getSectionHTML(sectionId, cache, url);
      if (sectionHTML && sectionHTML.trim() && !abortController.signal.aborted) {
        await morphSection(sectionId, sectionHTML);
      }
      return sectionHTML;
    } finally {
      if (this.#abortControllersBySectionId.get(sectionId) === abortController) {
        this.#abortControllersBySectionId.delete(sectionId);
      }
    }
  }

  /**
   * Invalidates reads and pending morphs superseded by a section mutation.
   * Existing requests may finish, but cannot repopulate the cache afterwards.
   * @param {string} sectionId - The section ID, with or without its DOM prefix
   */
  invalidateSection(sectionId) {
    if (typeof sectionId !== 'string' || !sectionId) return;
    const normalizedId = normalizeSectionId(sectionId);
    for (const [id, controller] of this.#abortControllersBySectionId) {
      if (normalizeSectionId(id) !== normalizedId) continue;
      controller.abort();
      this.#abortControllersBySectionId.delete(id);
    }
    for (const sectionUrl of new Set([...this.#cache.keys(), ...this.#pendingPromises.keys()])) {
      if (new URL(sectionUrl).searchParams.get('section_id') !== normalizedId) continue;
      this.#cache.delete(sectionUrl);
      this.#pendingPromises.delete(sectionUrl);
    }
  }

  /**
   * Aborts an existing morph for a section
   * @param {string} sectionId - The section ID
   */
  #abortPendingMorph(sectionId) {
    const existingAbortController = this.#abortControllersBySectionId.get(sectionId);
    if (existingAbortController) {
      existingAbortController.abort();
    }
  }

  /**
   * Gets the HTML for a section
   * @param {string} sectionId - The section ID
   * @param {boolean} useCache - Whether to use the cache
   * @param {URL} url - The URL to render the section for
   * @param {AbortSignal} [signal] - Optional cancellation for a page read
   * @returns {Promise<string>} The rendered section HTML
   */
  async getSectionHTML(sectionId, useCache = true, url = new URL(window.location.href), signal) {
    if (signal?.aborted) return '';
    const sectionUrl = buildSectionRenderingURL(sectionId, url);
    const isLocalThemeDevHost =
      window.location.hostname === 'localhost' ||
      window.location.hostname === '127.0.0.1' ||
      window.location.hostname === '::1';
    const shouldBypassHttpCache = Shopify.designMode || isLocalThemeDevHost;

    let pendingPromise = this.#pendingPromises.get(sectionUrl);
    if (pendingPromise) return pendingPromise;

    const cachedHTML = this.#cache.get(sectionUrl);
    if (useCache) {
      if (cachedHTML) return cachedHTML;
    }

    const requestController = new AbortController();
    const abortRequest = () => {
      requestController.abort();
      if (this.#pendingPromises.get(sectionUrl) === pendingPromise) this.#pendingPromises.delete(sectionUrl);
    };
    signal?.addEventListener('abort', abortRequest, { once: true });
    const requestTimeout = setTimeout(() => requestController.abort(), 15000);
    pendingPromise = fetch(sectionUrl, {
      ...(shouldBypassHttpCache ? { cache: 'no-store' } : {}),
      signal: requestController.signal,
    })
      .then(async (response) => {
        if (response.status === 304) return cachedHTML || '';

        const responseText = await response.text();
        const isChallengeHTML =
          /se debe verificar|verificar tu conexi|checking if the site connection|shopify-challenge|cf-challenge/i.test(
            responseText
          );
        const isBlockedStatus = response.status === 403 || response.status === 429 || response.status === 502;

        if (isBlockedStatus || isChallengeHTML) {
          console.warn(`Section rendering skipped after storefront protection response (${response.status})`);
          return cachedHTML || '';
        }

        if (!response.ok) throw new Error(`Section rendering failed with status ${response.status}`);
        return responseText;
      })
      .then((sectionHTML) => {
        if (this.#pendingPromises.get(sectionUrl) === pendingPromise && sectionHTML && sectionHTML.trim()) {
          this.#cache.set(sectionUrl, sectionHTML);
        }
        return sectionHTML;
      })
      .finally(() => {
        clearTimeout(requestTimeout);
        signal?.removeEventListener('abort', abortRequest);
        if (this.#pendingPromises.get(sectionUrl) === pendingPromise) {
          this.#pendingPromises.delete(sectionUrl);
        }
      });

    this.#pendingPromises.set(sectionUrl, pendingPromise);

    return pendingPromise;
  }

  /**
   * Caches the page sections
   */
  #cachePageSections() {
    for (const section of document.querySelectorAll('.shopify-section')) {
      const url = buildSectionRenderingURL(section.id);
      if (this.#cache.get(url)) return;
      if (containsShadowRoot(section)) return;

      this.#cache.set(url, section.outerHTML);
    }
  }
}

const SECTION_ID_PREFIX = 'shopify-section-';

/**
 * Builds a section rendering URL
 * @param {string} sectionId - The section ID
 * @param {URL} url - The URL to render the section for
 * @returns {string} The section rendering URL
 */
function buildSectionRenderingURL(sectionId, url = new URL(window.location.href)) {
  url.searchParams.set('section_id', normalizeSectionId(sectionId));
  url.searchParams.sort();

  return url.toString();
}

/**
 * Builds a section selector
 * @param {string} sectionId - The section ID
 * @returns {string} The section selector
 */
export function buildSectionSelector(sectionId) {
  return `${SECTION_ID_PREFIX}${sectionId}`;
}

/**
 * Normalizes a section ID
 * @param {string} sectionId - The section ID
 * @returns {string} The normalized section ID
 */
export function normalizeSectionId(sectionId) {
  return sectionId.replace(new RegExp(`^${SECTION_ID_PREFIX}`), '');
}

/**
 * Checks if an element contains a shadow root
 * @param {Element} element - The element to check
 * @returns {boolean} Whether the element contains a shadow root
 */
function containsShadowRoot(element) {
  return !!element.shadowRoot || Array.from(element.children).some(containsShadowRoot);
}

/**
 * @typedef {(previousElement: HTMLElement, newElement: HTMLElement) => void} UpdateCallback
 */

/**
 * Morphs the existing section element with the new section contents
 *
 * @param {string} sectionId - The section ID
 * @param {string} html - The new markup the section should morph into
 */
export async function morphSection(sectionId, html) {
  const fragment = new DOMParser().parseFromString(html, 'text/html');
  const existingElement = document.getElementById(buildSectionSelector(sectionId));
  const newElement = fragment.getElementById(buildSectionSelector(sectionId));

  if (!existingElement) {
    throw new Error(`Section ${sectionId} not found`);
  }

  if (!newElement) {
    throw new Error(`Section ${sectionId} not found in the section rendering response`);
  }

  morph(existingElement, newElement);
}

export const sectionRenderer = new SectionRenderer();
